/**
 * What a campaign or a template is written as.
 *
 * Two kinds, because two genuinely different things arrive here and collapsing
 * them would lose the one that can be edited again.
 *
 * **Prose** is a person typing into a box: paragraphs separated by blank lines,
 * escaped on the way out. It is what every campaign written before the builder
 * existed is made of, and it stays a first-class kind rather than being
 * converted once and forgotten — somebody who wants to write four sentences and
 * send them should not have to open a layout editor.
 *
 * **Designed** is the block builder's output. `html` is what goes in the
 * message; `source` is the builder's own project state, kept so the design can
 * be reopened and changed. Without `source` a design could be sent and never
 * edited again, which is how the predecessor ended up with templates nobody
 * could alter.
 *
 * The html is **not trusted**. It is authored in a browser, or by a language
 * model, and it is sanitised where it is rendered rather than here — this
 * module is pure and a real sanitiser needs a parser. What this type does
 * guarantee is that nowhere in the system is there a second idea of what a body
 * is.
 */
/** The only way to make one from a textarea. */
export function proseBody(text) {
    return { kind: 'prose', text };
}
/**
 * The only way to make one from the builder.
 *
 * `source` may be empty: a design that arrived from the AI designer as finished
 * html has no builder project behind it until somebody opens it in the builder
 * and saves. Empty is that state, and it is honest — the alternative is
 * inventing a project file that describes markup the builder did not lay out.
 */
export function designedBody(html, source) {
    return { kind: 'designed', html, source };
}
/**
 * Whether there is anything to send.
 *
 * Asked before a campaign is saved and on the pre-send checklist. A designed
 * body that is only an empty wrapper is as empty as an empty textarea, and a
 * message with nothing in it is the one mistake a preview does not catch,
 * because an empty design still renders a header and a footer.
 */
export function isEmptyBody(body) {
    return body.kind === 'prose' ? body.text.trim() === '' : textFromHtml(body.html).trim() === '';
}
/**
 * The opening words, for an inbox preview line.
 *
 * Taken from the body rather than from the subject, because the two appear
 * side by side in every mail client and repeating the subject wastes the only
 * line a reader gets before deciding.
 */
export function firstLineOf(body) {
    const source = body.kind === 'prose' ? body.text : textFromHtml(body.html);
    return (source.trim().split('\n')[0] ?? '').slice(0, 140);
}
/**
 * The words out of markup, for the plain-text part and the preview line.
 *
 * Not a sanitiser and not a renderer — it is deliberately crude, because its
 * job is to answer "what does this say" rather than to reproduce a layout. A
 * link becomes its text followed by its address in brackets: a plain-text
 * reader who cannot see the anchor still needs somewhere to click, and a
 * message whose text part silently drops every url reads as spam to a filter
 * comparing the two halves.
 *
 * Block-level tags become line breaks so paragraphs stay apart. Everything
 * else is dropped.
 */
export function textFromHtml(html) {
    return decodeEntities(html
        // Whole elements whose content is not prose at all.
        .replace(/<(script|style|head|title)\b[^>]*>[\s\S]*?<\/\1>/gi, '')
        .replace(/<a\b[^>]*\bhref\s*=\s*["']([^"']*)["'][^>]*>([\s\S]*?)<\/a>/gi, (_whole, href, inner) => {
        const label = inner.replace(/<[^>]*>/g, '').trim();
        // A link whose text is already the address prints once rather than
        // twice, which is what a person writing it meant.
        return label === '' || label === href ? href : `${label} (${href})`;
    })
        .replace(/<br\b[^>]*>/gi, '\n')
        .replace(/<\/(p|div|tr|h[1-6]|li|table|section)\s*>/gi, '\n\n')
        .replace(/<[^>]*>/g, ''))
        .replace(/[ \t]+/g, ' ')
        .replace(/ *\n */g, '\n')
        .replace(/\n{3,}/g, '\n\n')
        .trim();
}
/**
 * The five entities that actually appear, and numeric ones.
 *
 * Not a full entity table: this runs over markup a builder produced, which
 * escapes exactly what it has to. A name list would be a long thing to keep
 * true, and getting one wrong shows up as `&eacute;` in a plain-text email.
 */
function decodeEntities(text) {
    return (text
        .replace(/&#(\d+);/g, (_whole, code) => String.fromCodePoint(Number(code)))
        .replace(/&#x([0-9a-f]+);/gi, (_whole, code) => String.fromCodePoint(Number.parseInt(code, 16)))
        .replace(/&nbsp;/gi, ' ')
        .replace(/&quot;/gi, '"')
        .replace(/&#39;|&apos;/gi, "'")
        .replace(/&lt;/gi, '<')
        .replace(/&gt;/gi, '>')
        // Last, or it would turn `&amp;lt;` into `<`.
        .replace(/&amp;/gi, '&'));
}
//# sourceMappingURL=emailBody.js.map